import { useState } from "react";
import { API_URL } from "../../context/AuthContext";
import formatPrice from "../../utils/formatPrice";
import TranslateAllButton from "./TranslateAllButton";
import {
  Box,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import type { ProductData } from "./Dashboard";

interface OptionsSectionProps {
  product: ProductData;
  token: string | null;
  onChange: () => void;
}

export default function OptionsSection({ product, token, onChange }: OptionsSectionProps) {
  const [newName, setNewName] = useState("");
  const [newNameEn, setNewNameEn] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editNameEn, setEditNameEn] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [msg, setMsg] = useState("");

  const options = product.options;

  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const handleSave = async (id: number) => {
    try {
      await fetch(`${API_URL}/api/options/${id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ name: editName, name_en: editNameEn, amount: parseInt(editAmount) || 0 }),
      });
      setEditingId(null);
      setMsg("Zapisano");
      onChange();
    } catch {
      setMsg("Błąd zapisu");
    }
  };

  const handleAdd = async () => {
    if (!newName || !newAmount) return;
    try {
      await fetch(`${API_URL}/api/products/${product.id}/options`, {
        method: "POST",
        headers,
        body: JSON.stringify({ name: newName, name_en: newNameEn, amount: parseInt(newAmount) || 0 }),
      });
      if (!newNameEn.trim()) {
        await fetch(`${API_URL}/api/products/${product.id}/translate-related`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setNewName("");
      setNewNameEn("");
      setNewAmount("");
      setMsg("Dodano opcję i przetłumaczono");
      onChange();
    } catch {
      setMsg("Błąd dodawania");
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Usunąć tę opcję?")) return;
    try {
      await fetch(`${API_URL}/api/options/${id}`, { method: "DELETE", headers });
      setMsg("Usunięto");
      onChange();
    } catch {
      setMsg("Błąd usuwania");
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {msg && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMsg("")}>{msg}</Alert>}
      <TranslateAllButton product={product} token={token} onTranslated={onChange} />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: "#aaa" }}>Nazwa (PL)</TableCell>
              <TableCell sx={{ color: "#aaa" }}>Nazwa (EN)</TableCell>
              <TableCell sx={{ color: "#aaa" }} align="right">Cena</TableCell>
              <TableCell sx={{ color: "#aaa" }} align="right">Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {options.map((o) => (
              <TableRow key={o.id}>
                {editingId === o.id ? (
                  <>
                    <TableCell>
                      <TextField size="small" value={editName} onChange={(e) => setEditName(e.target.value)} sx={{ input: { color: "#f0f0f0" } }} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={editNameEn} onChange={(e) => setEditNameEn(e.target.value)} placeholder="EN" sx={{ input: { color: "#f0f0f0" } }} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" value={editAmount} onChange={(e) => setEditAmount(e.target.value)} sx={{ input: { color: "#f0f0f0" } }} />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => handleSave(o.id)} sx={{ color: "#4caf50" }}>Zapisz</Button>
                      <Button size="small" onClick={() => setEditingId(null)} sx={{ color: "#aaa" }}>Anuluj</Button>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell sx={{ color: "#f0f0f0" }}>{o.name}</TableCell>
                    <TableCell sx={{ color: "#aaa", fontSize: "0.85rem" }}>{o.name_en || "—"}</TableCell>
                    <TableCell align="right" sx={{ color: "#4caf50", fontWeight: 700 }}>{formatPrice(o.amount)}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => { setEditingId(o.id); setEditName(o.name); setEditNameEn(o.name_en || ""); setEditAmount(String(o.amount)); }} sx={{ color: "#1976d2" }}>Edytuj</Button>
                      <IconButton size="small" onClick={() => handleDelete(o.id)} sx={{ color: "#f44336" }}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: "flex", gap: 1, mt: 2, alignItems: "center" }}>
        <TextField size="small" placeholder="Nazwa opcji PL" value={newName} onChange={(e) => setNewName(e.target.value)} sx={{ flex: 2, input: { color: "#f0f0f0" } }} />
        <TextField size="small" placeholder="Nazwa opcji EN" value={newNameEn} onChange={(e) => setNewNameEn(e.target.value)} sx={{ flex: 2, input: { color: "#f0f0f0" } }} />
        <TextField size="small" type="number" placeholder="Cena" value={newAmount} onChange={(e) => setNewAmount(e.target.value)} sx={{ flex: 1, input: { color: "#f0f0f0" } }} />
        <Button size="small" startIcon={<AddIcon />} onClick={handleAdd} sx={{ color: "#4caf50" }}>Dodaj</Button>
      </Box>
    </Box>
  );
}
